import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  ViewEncapsulation,
} from "@angular/core";
import {
  createChart,
  CrosshairMode,
  LineStyle,
  IChartApi,
  ISeriesApi,
} from "lightweight-charts";
import { HttpClient } from "@angular/common/http";
import { Subject } from "rxjs";
import { debounceTime, switchMap, catchError } from "rxjs/operators";
import { of } from "rxjs";

import { InsaService, LocalizeService, AuthService } from "../../insa-services";
import { ActivatedRoute } from "@angular/router";
import { appStateProvider } from "../../insa-providers";

import { appConfig } from "../../../insaweb.config";
import { AppComponent } from "../../app.component";

interface TradeMarker {
  time: any;
  position: "aboveBar" | "belowBar";
  color: string;
  shape: "circle" | "square" | "arrowUp" | "arrowDown";
  text: string;
}

interface DrawingTool {
  id: string;
  name: string;
  icon: string;
  active: boolean;
}

interface Drawing {
  id: string;
  type: string;
  points: Array<{ time: number; price: number }>;
  color: string;
}

interface TraderSecurityDtoModel {
  SecIndex: number;
  SecNumber: string;
  SecISIN: string;
  SecStockExchange: number;
  SecSymbol: string;
  SecName: string;
  SecCurrSymbol: string;
  FxRate: number;
  StockExchangeCode: string;
  StockExchangeName: string;
  EODHDStockExchangeCode: string;
  SecType: number;
  SecCurr: number;
  SecFactor: number;
  SecContractSize: number;
  fromDate?: string;
  toDate?: string;
}

interface EODHDEODResponseModel {
  date: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  adjusted_close: number | null;
  volume: number | null;
}

interface TraderOrderResponseModel {
  success: boolean;
  description: string;
}

interface EODHDRealTimeResponseModel {
  price: number | null;
  bid: number | null;
  bidQuantity: number | null;
  ask: number | null;
  askQuantity: number | null;
  level: number | null;
  lastClose: number | null;
}

@Component({
  selector: "app-insa-trader",
  templateUrl: "./insa-trader.component.html",
  styles: [require("./insa-trader.component.css").toString()],
  encapsulation: ViewEncapsulation.None,
})
export class InsaTraderComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private _localize: LocalizeService,
    private _element: ElementRef,
    private _insaService: InsaService,
    private _authService: AuthService,
    private _http: HttpClient,
    private _route: ActivatedRoute,
    private _appStateProvider: appStateProvider,
  ) {}

  @ViewChild("chartContainer", { static: false }) chartContainer: ElementRef;
  @ViewChild("drawingCanvas", { static: false }) drawingCanvas: ElementRef;

  private traderApiBase = appConfig.insaServiceAddress + "/Trader";
  private chart: IChartApi;
  private candlestickSeries: ISeriesApi<"Candlestick">;
  private volumeSeries: any;
  private ema20Series: any;
  private ema50Series: any;
  private ema200Series: any;
  private bbUpperSeries: any;
  private bbLowerSeries: any;
  private resizeObserver: any;
  private drawingRedrawInterval: any;
  private searchSubject = new Subject<string>();
  private searchSubscription: any;
  private _orderTypesToken = 0;
  private _allowedOrderTypesToken = 0;
  allowedOrderTypes: number[] = [];

  private ctx: CanvasRenderingContext2D;
  private isDrawing = false;
  private drawingStartPoint: {
    x: number;
    y: number;
    time: number;
    price: number;
  } | null = null;
  private drawingColor = "#2962FF";

  drawingTools: DrawingTool[] = [];

  selectedDrawingTool: string | null = null;
  drawings: Drawing[] = [];
  selectedSecurity: TraderSecurityDtoModel | null = null;
  securityNotAvailableToast = false;
  private securityNotAvailableToastTimeout: any = null;
  selectedSymbol = "";
  selectedAssetName = "";
  currentPrice = 0;
  priceChange = 0;
  priceChangePercent = 0;
  isLoadingChart = false;
  isCheckingOrder = false;
  isSubmittingOrder = false;
  orderError = "";

  liveCheckAmounts: { [key: string]: number } | null = null;
  liveCheckLoading = false;
  private liveCheckSubscription: any;
  private liveCheckSubject = new Subject<void>();
  heldQuantity: number = 0;
  heldQuantityLoading: boolean = false;
  showOrderConfirm = false;
  confirmOrderSide: "Buy" | "Sell" | null = null;
  orderResult: { success: boolean; description: string } | null = null;
  orderCheckAmounts: { [key: string]: number } | null = null;
  orderCheckError = "";
  priceL1: EODHDRealTimeResponseModel | null = null;
  priceL2: EODHDRealTimeResponseModel[] = [];
  private realtimePollInterval: any;
  private secReservationPercent: number = 1;

  timeframes = ["1W", "1M", "6M", "1Y", "3Y"];

  indicators = [
    { name: "EMA 20", enabled: true, color: "#2962FF" },
    { name: "EMA 50", enabled: true, color: "#FF6D00" },
    { name: "EMA 200", enabled: true, color: "#E91E63" },
    { name: "Bollinger Bands", enabled: true, color: "#9C27B0" },
    { name: "Volume", enabled: true, color: "#26A69A" },
  ];

  favorites: TraderSecurityDtoModel[] = [];
  filteredAssets: TraderSecurityDtoModel[] = [];
  searchQuery = "";
  showSearchResults = false;
  searchFocused = false;

  selectedOrderType: number = 2;
  tradePrice = 0;
  tradeStopPrice = 0;
  tradeAmount = 0;
  tradeTotal = 0;
  sliderPercent = 0;

  isDomestic = true;
  allowedTransactionTypes: number[] = [];
  selectedValidity = "Day";
  goodTillDate = "";

  selectedTimeframe = "1Y";

  recentTrades: any[] = [];

  private currentCandleData: any[] = [];
  private currentVolumeData: any[] = [];
  private currentEma20Data: any[] = [];
  private currentEma50Data: any[] = [];
  private currentEma200Data: any[] = [];
  private currentBBData: any = { upper: [], lower: [] };

  private reportSubscription: any;
  private settingsSubscription: any;
  private settingsRefreshTimeout: any;
  private _cashBalanceToken = 0;
  private _positionQuantityToken = 0;

  availableLiquidity: number = 0;
  liquidityCurrency: string = "";
  cashBalanceLoading: boolean = false;
  isSettingsRefreshing: boolean = false;

  checkCashBalance: boolean = true;

  get securityCurrency(): string {
    return (
      (this.selectedSecurity && this.selectedSecurity.SecCurrSymbol) ||
      this.liquidityCurrency
    );
  }

  get isFavorite(): boolean {
    return (
      !!this.selectedSecurity &&
      this.favorites.some((f) => f.SecIndex === this.selectedSecurity.SecIndex)
    );
  }

  private get fxRate(): number {
    return this.selectedSecurity && this.selectedSecurity.FxRate > 0
      ? this.selectedSecurity.FxRate
      : 1;
  }

  private get secFactor(): number {
    return this.selectedSecurity && this.selectedSecurity.SecFactor > 0
      ? this.selectedSecurity.SecFactor
      : 1;
  }

  private get secContractSize(): number {
    return this.selectedSecurity && this.selectedSecurity.SecContractSize > 0
      ? this.selectedSecurity.SecContractSize
      : 1;
  }

  private get effectivePrice(): number {
    return this.isImmediateOrder ? this.currentPrice : this.tradePrice;
  }

  get exceedsLiquidity(): boolean {
    // Cash check disabled for this partner — never flag a BUY as over-liquidity.
    if (!this.checkCashBalance) return false;
    // tradeTotal is in security currency; convert to liquidity currency for comparison
    return (
      this.availableLiquidity > 0 &&
      this.tradeTotal * this.fxRate > this.availableLiquidity
    );
  }

  get noFunds(): boolean {
    if (!this.checkCashBalance) return false;
    return this.availableLiquidity <= 0 && this.tradeAmount > 0;
  }

  get marketOnlyForSell(): boolean {
    return !this.isDomestic && this.selectedOrderType === 1;
  }

  get buyAllowedForType(): boolean {
    return (
      this.allowedOrderTypes.length === 0 || this.allowedOrderTypes.includes(0)
    );
  }

  get sellAllowedForType(): boolean {
    return (
      this.allowedOrderTypes.length === 0 || this.allowedOrderTypes.includes(1)
    );
  }

  get canTrade(): boolean {
    return (
      this.tradeAmount > 0 &&
      !this.exceedsLiquidity &&
      !this.noFunds &&
      !this.isSubmittingOrder &&
      !!this.selectedSecurity &&
      !this.marketOnlyForSell &&
      this.buyAllowedForType &&
      (this.isImmediateOrder || this.tradePrice > 0)
    );
  }

  get noHoldings(): boolean {
    return !!this.selectedSecurity && this.heldQuantity <= 0;
  }

  get exceedsSellAmount(): boolean {
    return this.heldQuantity > 0 && this.tradeAmount > this.heldQuantity;
  }

  get canSell(): boolean {
    return (
      this.tradeAmount > 0 &&
      !this.noHoldings &&
      !this.exceedsSellAmount &&
      !this.isSubmittingOrder &&
      !!this.selectedSecurity &&
      this.sellAllowedForType &&
      (this.isImmediateOrder || this.tradePrice > 0)
    );
  }

  get sliderDisabled(): boolean {
    return this.availableLiquidity <= 0;
  }

  // L1/L2 prices as sent by the backend: 2-4 decimals, trailing zeros past
  // the 2nd dropped (3.2000 -> 3.20, 3.2330 -> 3.233, 3.2333 -> 3.2333)
  formatQuote(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value as number))
      return "";
    return (kendo as any).toString(value, "#,##0.00##");
  }

  formatN(value: number | null | undefined, decimals: number = 2): string {
    if (value === null || value === undefined || isNaN(value as number))
      return "";
    return (kendo as any).toString(value, "n" + decimals);
  }

  formatDateShort(value: string | Date | null | undefined): string {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    return (kendo as any).toString(d, "dd.MM.yyyy");
  }

  onValidityChange(): void {
    if (!this.showDatePicker) {
      this.goodTillDate = "";
    } else {
      setTimeout(() => this.initGtdDatePickers(), 0);
    }
  }

  private initGtdDatePickers(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentValue = this.goodTillDate ? new Date(this.goodTillDate) : null;
    const culture = this._localize.getLocale
      ? this._localize.getLocale()
      : undefined;

    const makeOptions = (siblingId: string): kendo.ui.DatePickerOptions => ({
      format: "dd.MM.yyyy",
      min: today,
      value: currentValue,
      culture,
      change: (evt) => {
        const d: Date = evt.sender.value();
        if (d) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          this.goodTillDate = `${y}-${m}-${day}`;
          const sibling = ($(`#${siblingId}`) as any).data("kendoDatePicker");
          if (sibling) sibling.value(d);
        } else {
          this.goodTillDate = "";
        }
      },
    });

    for (const [id, siblingId] of [
      ["gtdDateDesktop", "gtdDateMobile"],
      ["gtdDateMobile", "gtdDateDesktop"],
    ] as [string, string][]) {
      const $el = $(`#${id}`) as any;
      if ($el.length) {
        if ($el.data("kendoDatePicker")) $el.data("kendoDatePicker").destroy();

        const options = {
          ...makeOptions(siblingId),
          month: {
            empty: '<a class="k-link k-past-date-disabled">#= data.value #</a>',
          },
        };

        $el.kendoDatePicker(options);
      }
    }
  }

  toggleFavorite(): void {
    if (!this.selectedSecurity) return;
    const headers = this._authService.getAuthHeaders("application/json");
    if (this.isFavorite) {
      this._http
        .delete<TraderOrderResponseModel>(
          this.traderApiBase + "/TraderFavouritesDelete",
          { headers, body: this.selectedSecurity } as any,
        )
        .subscribe({
          next: () => {
            this.favorites = this.favorites.filter(
              (f) => f.SecIndex !== this.selectedSecurity.SecIndex,
            );
          },
          error: (err) => console.error("TraderFavouritesDelete error:", err),
        });
    } else {
      this._http
        .post<TraderOrderResponseModel>(
          this.traderApiBase + "/TraderFavouritesInsert",
          this.selectedSecurity,
          { headers },
        )
        .subscribe({
          next: () => {
            this.favorites = [...this.favorites, this.selectedSecurity];
          },
          error: (err) => console.error("TraderFavouritesInsert error:", err),
        });
    }
  }

  onFavoriteSelect(event: any): void {
    const secIndex = parseInt(event.target.value, 10);
    const fav = this.favorites.find((f) => f.SecIndex === secIndex);
    if (fav) this.selectAsset(fav);
  }

  private readonly transactionTypeLabels: Record<number, string> = {
    1: "Market",
    2: "Limit",
    3: "Limit Fill or Kill",
    4: "Limit FAK",
    5: "Market Fill or Kill",
    6: "Stop Loss",
    7: "Stop Limit",
    8: "On Stop",
    9: "Short Exempt",
    10: "Short Sales",
    11: "Accept Order",
    12: "Market on Close",
    13: "Market Anonymous",
    14: "Limit Anonymous",
    15: "Limit Iceberg",
    16: "Give",
    17: "Take",
    18: "FX Buy",
    19: "FX Sell",
    20: "REPO",
    21: "Reverse REPO",
    22: "Transfer Order",
    23: "Take Correction",
    24: "Block Trade",
    25: "VWAP",
    26: "Pre Market",
    27: "After Market",
    28: "Imm. or Cancel",
  };

  // Getteri ispod vraćaju nove objekte pri svakom change detectionu; bez
  // trackBy bi ngFor stalno iznova kreirao <option> elemente i otvoreni
  // padajući meni ne bi prihvatio odabir.
  trackById(_index: number, item: { id: number | string }): number | string {
    return item.id;
  }

  trackBySecIndex(_index: number, item: { SecIndex: number }): number {
    return item.SecIndex;
  }

  get orderTypeItems(): { id: number; label: string }[] {
    return this.allowedTransactionTypes.map((type) => ({
      id: type,
      label: this.transactionTypeLabels[type] ?? `Type ${type}`,
    }));
  }

  get selectedOrderTypeLabel(): string {
    return this.transactionTypeLabels[this.selectedOrderType] ?? "";
  }

  get isImmediateOrder(): boolean {
    return [1, 5, 28].includes(this.selectedOrderType);
  }

  get validityOptions(): { id: string; label: string }[] {
    if (this.isImmediateOrder) return [];
    const day = {
      id: "Day",
      label: this._localize.getLocalizedResource("Res3330") || "Dan",
    };
    const gtd = {
      id: "GTD",
      label: this._localize.getLocalizedResource("Res3331") || "Do datuma",
    };
    const gtc = {
      id: "GTC",
      label: this._localize.getLocalizedResource("Res3332") || "Do opoziva",
    };
    if (this.isDomestic) return [day, gtd];
    if (this.selectedOrderType === 26 || this.selectedOrderType === 27)
      return [day];
    if ([2, 3, 4, 7].includes(this.selectedOrderType)) return [day, gtd, gtc];

    return [day, gtd, gtc];
  }

  get showStopPriceField(): boolean {
    return this.selectedOrderType === 7 && !this.isDomestic;
  }

  get priceLabelForCurrentType(): string {
    if (this.selectedOrderType === 7)
      return this._localize.getLocalizedResource("Res572") || "Limit Cena";
    return this._localize.getLocalizedResource("Res3534") || "Price";
  }

  get showDatePicker(): boolean {
    return !this.isImmediateOrder && this.selectedValidity === "GTD";
  }

  get todayDateString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }

  get favoritesPlaceholder(): string {
    const label = this._localize.getLocalizedResource("Res3994") || "odaberi";
    return `— ${label} —`;
  }

  get selectInstrumentText(): string {
    return (
      this._localize.getLocalizedResource("Res1132") || "Choose instrument"
    );
  }

  get orderBookNoDataText(): string {
    return (
      this._localize.getLocalizedResource("Res1131") ||
      "No information in the order book"
    );
  }

  get favoriteTitle(): string {
    return this.isFavorite
      ? this._localize.getLocalizedResource("Res1113") || "Ukloni iz favorita"
      : this._localize.getLocalizedResource("Res1114") || "Dodaj u favorite";
  }

  get favoritesHint(): string {
    return (
      this._localize.getLocalizedResource("Res1118") ||
      "Pretraži i dodaj ★ u favorite"
    );
  }

  get searchPlaceholder(): string {
    return (
      this._localize.getLocalizedResource("Res1120") ||
      "e.g. TSLA, AMZN, AAPL..."
    );
  }

  get cancelLastTitle(): string {
    return this._localize.getLocalizedResource("Res1127") || "Cancel last";
  }

  get deleteAllTitle(): string {
    return this._localize.getLocalizedResource("Res1128") || "Delete all";
  }

  get processingText(): string {
    return this._localize.getLocalizedResource("Res16") || "Processing...";
  }

  get orderResultMessage(): string {
    if (!this.orderResult) return "";
    if (this.orderResult.description) return this.orderResult.description;
    return this.orderResult.success
      ? this._localize.getLocalizedResource("Res489") ||
          "Order submitted successfully."
      : this._localize.getLocalizedResource("Res490") || "Order failed.";
  }

  get selectedValidityLabel(): string {
    const opt = this.validityOptions.find(
      (v) => v.id === this.selectedValidity,
    );
    return opt ? opt.label : this.selectedValidity;
  }

  private getFeeLabel(key: string): string {
    const map: { [k: string]: string } = {
      Bruto: "Res1147",
      PROVBROK: "Res1148",
      PROVSALD: "Res1149",
      POREZ: "Res1150",
    };
    const resId = map[key];
    return (resId && this._localize.getLocalizedResource(resId)) || key;
  }

  get orderCheckFeeRows(): { key: string; value: number }[] {
    if (!this.orderCheckAmounts) return [];
    return Object.keys(this.orderCheckAmounts)
      .filter((k) => k !== "Ukupno")
      .map((k) => ({
        key: this.getFeeLabel(k),
        value: Math.abs(this.orderCheckAmounts[k]), // always positive, in security currency
      }))
      .filter((row) => row.value !== 0); // skip zero-value items (e.g. tax = 0)
  }

  get orderCheckTotal(): number | null {
    if (!this.orderCheckAmounts) return null;
    return (this.orderCheckAmounts["Ukupno"] ?? 0) * this.fxRate;
  }

  // Neto in security currency (no FX conversion)
  get orderCheckNet(): number | null {
    if (!this.orderCheckAmounts) return null;
    return this.orderCheckAmounts["Ukupno"] ?? 0;
  }

  get liveFeeRows(): { key: string; value: number }[] {
    if (!this.liveCheckAmounts) return [];
    return Object.keys(this.liveCheckAmounts)
      .filter((k) => k !== "Ukupno")
      .map((k) => ({
        key: this.getFeeLabel(k),
        value: Math.abs(this.liveCheckAmounts[k]), // always positive, in security currency
      }))
      .filter((row) => row.value !== 0); // skip zero-value items (e.g. tax = 0)
  }

  get liveCheckTotal(): number | null {
    if (!this.liveCheckAmounts) return null;
    return (this.liveCheckAmounts["Ukupno"] ?? 0) * this.fxRate;
  }

  // Neto in security currency (no FX conversion)
  get liveCheckNet(): number | null {
    if (!this.liveCheckAmounts) return null;
    return this.liveCheckAmounts["Ukupno"] ?? 0;
  }

  get priceTimesQuantity(): number {
    return (
      (this.tradeAmount *
        (this.isImmediateOrder ? this.currentPrice : this.tradePrice) *
        this.secContractSize) /
      this.secFactor
    );
  }

  onOrderTypeChange(type: number): void {
    this.selectedOrderType = +type; // ensure number, Angular ngModelChange can pass string
    this.selectedValidity = this.isImmediateOrder ? "Immediate" : "Day";
    if (!this.showDatePicker) this.goodTillDate = "";
    this.reapplyLocalize(); // re-translate validity label/options rendered via *ngIf
    if (this.selectedSecurity) {
      this.allowedOrderTypes = [];
      this.loadAllowedOrderTypesForTransaction(
        this.selectedSecurity.SecStockExchange,
        +type,
      );
    }
    // Clear stale fee calculation — fees depend on order type and price model
    this.liveCheckAmounts = null;
    this.liveCheckLoading = false;
    // Recalculate total for the new order type's price model (immediate uses mid price, limit uses entered price)
    const price = this.isImmediateOrder ? this.currentPrice : this.tradePrice;
    this.tradeTotal =
      price > 0
        ? (price * this.tradeAmount * this.secContractSize) / this.secFactor
        : 0;
    // Trigger fresh live check if there's a computable order
    if (this.tradeAmount > 0 && this.selectedSecurity) {
      this.liveCheckLoading = true;
      this.liveCheckSubject.next();
    }
  }

  ngOnInit(): void {
    this.drawingTools = [
      {
        id: "trendline",
        name: this._localize.getLocalizedResource("Res2744") || "Linija trenda",
        icon: "📈",
        active: false,
      },
      {
        id: "horizontal",
        name:
          this._localize.getLocalizedResource("Res2745") ||
          "Horizontalna linija",
        icon: "━",
        active: false,
      },
      {
        id: "vertical",
        name:
          this._localize.getLocalizedResource("Res2746") || "Vertikalna linija",
        icon: "┃",
        active: false,
      },
      {
        id: "rectangle",
        name: this._localize.getLocalizedResource("Res2747") || "Pravougaonik",
        icon: "▭",
        active: false,
      },
      {
        id: "fibonacci",
        name:
          this._localize.getLocalizedResource("Res2748") ||
          "Fibonačijevo povlačenje",
        icon: "Φ",
        active: false,
      },
      {
        id: "arrow",
        name: this._localize.getLocalizedResource("Res2749") || "Strelica",
        icon: "→",
        active: false,
      },
    ];

    this.loadCheckCashBalanceForUser();

    const defaultCurrency = appConfig.initialSettings?.currency ?? "";
    this.liquidityCurrency =
      this._appStateProvider.selectedCurrency.text ||
      this._insaService.report?.ReportingCurrency ||
      defaultCurrency;
    this.reportSubscription = this._insaService.CreateReportEvent.subscribe(
      () => {
        this.liquidityCurrency =
          this._appStateProvider.selectedCurrency.text || defaultCurrency;
        if (this.isSettingsRefreshing) {
          clearTimeout(this.settingsRefreshTimeout);
          this.isSettingsRefreshing = false;
          this.loadFavorites(undefined, true);
          if (this.selectedSecurity) {
            this.loadCashBalanceForSecurity(this.selectedSecurity);
          }
        }
      },
    );
    this.settingsSubscription = this._appStateProvider.subscribe(() => {
      this.isSettingsRefreshing = true;
      if (this.selectedSecurity) {
        this.cashBalanceLoading = true;
        this.availableLiquidity = 0;
      }
      this.liveCheckAmounts = null;
      clearTimeout(this.settingsRefreshTimeout);
      this.settingsRefreshTimeout = setTimeout(() => {
        this.isSettingsRefreshing = false;
        this.cashBalanceLoading = false;
      }, 20000);
    });

    const secNumberFromRoute =
      this._route.snapshot.queryParamMap.get("secNumber");
    if (secNumberFromRoute) {
      this.loadFavorites(secNumberFromRoute);
    } else {
      this.loadFavorites();
    }

    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        switchMap((query) => {
          if (!query || query.trim().length < 3) return of([]);
          const headers = this._authService.getAuthHeaders("application/json");
          return this._http
            .get<
              TraderSecurityDtoModel[]
            >(this.traderApiBase + "/TraderSecurities", { headers, params: { filter: query.trim(), currency: this.liquidityCurrency } })
            .pipe(catchError(() => of([])));
        }),
      )
      .subscribe((results) => {
        this.filteredAssets = results ?? [];
        this.reapplyLocalize();
      });

    this.liveCheckSubscription = this.liveCheckSubject
      .pipe(debounceTime(700))
      .subscribe(() => {
        this.runLiveCheck();
      });
  }

  private loadFavorites(targetSecNumber?: string, syncFxRate = false): void {
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .get<
        TraderSecurityDtoModel[]
      >(this.traderApiBase + "/TraderFavourites", { headers, params: { currency: this.liquidityCurrency } })
      .subscribe({
        next: (data) => {
          this.favorites = data ?? [];
          if (syncFxRate && this.selectedSecurity) {
            const updated = this.favorites.find(
              (f) => f.SecIndex === this.selectedSecurity.SecIndex,
            );
            if (updated) {
              this.selectedSecurity.FxRate = updated.FxRate;
            } else {
              this.syncFxRateFromApi();
            }
          }
          if (targetSecNumber) {
            this.loadSecurityByNumber(targetSecNumber);
          } else if (this.favorites.length > 0 && !this.selectedSecurity) {
            this.selectAsset(this.favorites[0]);
          }
        },
        error: (err) => {
          console.error("TraderFavourites error:", err);
          if (targetSecNumber) {
            this.loadSecurityByNumber(targetSecNumber);
          }
        },
      });
  }

  private syncFxRateFromApi(): void {
    if (!this.selectedSecurity) return;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .get<
        TraderSecurityDtoModel[]
      >(this.traderApiBase + "/TraderSecurities", { headers, params: { filter: this.selectedSecurity.SecNumber, currency: this.liquidityCurrency } })
      .subscribe({
        next: (data) => {
          if (!data || data.length === 0 || !this.selectedSecurity) return;
          const match = data.find(
            (s) => s.SecIndex === this.selectedSecurity.SecIndex,
          );
          if (match) {
            this.selectedSecurity.FxRate = match.FxRate;
          }
        },
      });
  }

  private loadSecurityByNumber(secNumber: string): void {
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .get<
        TraderSecurityDtoModel[]
      >(this.traderApiBase + "/TraderSecurities", { headers, params: { filter: secNumber, currency: this.liquidityCurrency } })
      .subscribe({
        next: (data) => {
          const match = data?.find(
            (s) => s.SecNumber.trim() === secNumber.trim(),
          );
          if (match) {
            this.selectAsset(match);
          } else {
            this.showSecurityNotAvailableToast();
          }
        },
        error: (err) => {
          console.error("Load security by number error:", err);
          this.showSecurityNotAvailableToast();
        },
      });
  }

  showSecurityNotAvailableToast(): void {
    clearTimeout(this.securityNotAvailableToastTimeout);
    this.securityNotAvailableToast = true;
    this.securityNotAvailableToastTimeout = setTimeout(() => {
      this.securityNotAvailableToast = false;
    }, 10000);
  }

  dismissSecurityNotAvailableToast(): void {
    clearTimeout(this.securityNotAvailableToastTimeout);
    this.securityNotAvailableToast = false;
  }

  private loadDefaultSecurity(): void {
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .get<
        TraderSecurityDtoModel[]
      >(this.traderApiBase + "/TraderSecurities", { headers, params: { filter: "TSLA", currency: this.liquidityCurrency } })
      .subscribe({
        next: (data) => {
          if (data && data.length > 0) {
            const tesla = data.find((s) => s.SecNumber === "TSLA") || data[0];
            this.selectAsset(tesla);
          }
        },
        error: (err) => console.error("Default security load error:", err),
      });
  }

  private reapplyLocalize(): void {
    setTimeout(() => {
      this._localize.applyTranslations(this._element.nativeElement);
    }, 0);
  }

  ngAfterViewInit(): void {
    this._localize.applyTranslations(this._element.nativeElement);
    setTimeout(() => {
      this.initChart();
      this.initDrawingCanvas();
      this.loadChartData();
      this.applyMobileIndicatorDefaults();
      this.setupRealtimeUpdates();
    }, 100);
  }

  @HostListener("window:insaThemeChange", ["$event"])
  onThemeChange(): void {
    if (!this.chart) return;
    const theme = this.getChartTheme();
    this.chart.applyOptions({
      layout: theme.layout,
      grid: theme.grid,
      crosshair: {
        vertLine: { color: theme.crosshair.vertLine.color },
        horzLine: { color: theme.crosshair.horzLine.color },
      },
      rightPriceScale: { borderColor: theme.border },
      timeScale: { borderColor: theme.border },
    });
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    clearInterval(this.drawingRedrawInterval);
    if (this.chart) {
      this.chart.remove();
    }
    if (this.reportSubscription) {
      this.reportSubscription.unsubscribe();
    }
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
    }
    clearTimeout(this.settingsRefreshTimeout);
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.liveCheckSubscription) {
      this.liveCheckSubscription.unsubscribe();
    }
    this.stopRealtimePolling();
    clearTimeout(this.securityNotAvailableToastTimeout);
  }

  private initChart(): void {
    if (!this.chartContainer?.nativeElement) return;
    const container = this.chartContainer.nativeElement;
    const theme = this.getChartTheme();

    this.chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight || 500,
      layout: theme.layout,
      grid: theme.grid,
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          width: 1,
          color: theme.crosshair.vertLine.color,
          style: LineStyle.Dashed,
        },
        horzLine: {
          width: 1,
          color: theme.crosshair.horzLine.color,
          style: LineStyle.Dashed,
        },
      },
      rightPriceScale: {
        borderColor: theme.border,
        scaleMargins: { top: 0.1, bottom: 0.2 },
      },
      timeScale: {
        borderColor: theme.border,
        timeVisible: true,
        secondsVisible: false,
      },
    });

    this.candlestickSeries = this.chart.addCandlestickSeries({
      upColor: "#1A8700",
      downColor: "#D13A3A",
      borderVisible: false,
      wickUpColor: "#1A8700",
      wickDownColor: "#D13A3A",
    });

    this.volumeSeries = this.chart.addHistogramSeries({
      color: "#1A8700",
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
    });

    this.chart.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    this.ema20Series = this.chart.addLineSeries({
      color: "#2962FF",
      lineWidth: 2,
      title: "EMA 20",
    });
    this.ema50Series = this.chart.addLineSeries({
      color: "#FF6D00",
      lineWidth: 2,
      title: "EMA 50",
    });
    this.ema200Series = this.chart.addLineSeries({
      color: "#E91E63",
      lineWidth: 2,
      title: "EMA 200",
    });

    this.bbUpperSeries = this.chart.addLineSeries({
      color: "rgba(156, 39, 176, 0.5)",
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: "BB Upper",
    });

    this.bbLowerSeries = this.chart.addLineSeries({
      color: "rgba(156, 39, 176, 0.5)",
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: "BB Lower",
    });

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (entries.length === 0 || entries[0].target !== container) return;
        const newRect = entries[0].contentRect;
        this.chart.applyOptions({
          width: newRect.width,
          height: newRect.height,
        });
        this.resizeDrawingCanvas();
      });
      this.resizeObserver.observe(container);
    }
  }
  private loadChartData(): void {
    if (!this.candlestickSeries || !this.selectedSecurity) return;

    const { fromDate, toDate } = this.getChartDateRange();
    const headers = this._authService.getAuthHeaders("application/json");
    const body: TraderSecurityDtoModel = Object.assign(
      {},
      this.selectedSecurity,
      {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
      },
    );

    this.isLoadingChart = true;
    this._http
      .post<
        EODHDEODResponseModel[]
      >(this.traderApiBase + "/TraderEODPricesHistory", body, { headers })
      .subscribe({
        next: (data) => {
          this.isLoadingChart = false;
          this.clearChartSeries();
          if (!data || data.length === 0) return;
          this.applyChartData(data, fromDate, toDate);
        },
        error: (err) => {
          this.isLoadingChart = false;
          console.error("TraderEODPricesHistory error:", err);
        },
      });
  }

  get l1Spread(): number {
    if (!this.priceL1 || this.priceL1.ask == null || this.priceL1.bid == null)
      return 0;
    return this.priceL1.ask - this.priceL1.bid;
  }

  get l2Rows(): { price: number; qty: number; isBid: boolean }[] {
    const rows: { price: number; qty: number; isBid: boolean }[] = [];
    for (const item of this.priceL2) {
      if (item.ask != null && item.ask > 0) {
        rows.push({
          price: item.ask,
          qty: item.askQuantity ?? 0,
          isBid: false,
        });
      }
      if (item.bid != null && item.bid > 0) {
        rows.push({ price: item.bid, qty: item.bidQuantity ?? 0, isBid: true });
      }
    }
    return rows;
  }

  get l2PairedRows(): {
    bidPrice: number | null;
    bidQty: number | null;
    askPrice: number | null;
    askQty: number | null;
  }[] {
    return this.priceL2
      .map((item) => ({
        bidPrice: item.bid != null && item.bid > 0 ? item.bid : null,
        bidQty:
          item.bid != null && item.bid > 0 ? (item.bidQuantity ?? 0) : null,
        askPrice: item.ask != null && item.ask > 0 ? item.ask : null,
        askQty:
          item.ask != null && item.ask > 0 ? (item.askQuantity ?? 0) : null,
      }))
      .filter((r) => r.bidPrice !== null || r.askPrice !== null);
  }

  private loadRealTimePricesL1(): void {
    if (!this.selectedSecurity) return;
    const security = this.selectedSecurity;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .post<
        EODHDRealTimeResponseModel[]
      >(this.traderApiBase + "/TraderRealTimePricesL1", security, { headers })
      .subscribe({
        next: (data) => {
          if (this.selectedSecurity !== security) return;
          if (data && data.length > 0) {
            this.priceL1 = data[0];
            // Always use the 'price' the backend sends — never a (bid+ask)/2
            // mid-price. For BELEX this is the price the backend provides, and
            // for foreign markets it is already computed on the backend too.
            const livePrice = this.priceL1.price;
            if (livePrice && livePrice > 0) {
              this.currentPrice = livePrice;
              const referencePrice =
                this.priceL1.lastClose && this.priceL1.lastClose > 0
                  ? this.priceL1.lastClose
                  : this.currentPrice;
              this.priceChange = this.currentPrice - referencePrice;
              this.priceChangePercent =
                referencePrice > 0
                  ? (this.priceChange / referencePrice) * 100
                  : 0;
              if (this.isImmediateOrder) {
                this.tradePrice = Math.round(this.currentPrice * 100) / 100;
              }
            }
          } else {
            this.priceL1 = null;
          }
        },
        error: (err) => console.error("TraderRealTimePricesL1 error:", err),
      });
  }

  private loadRealTimePricesL2(): void {
    if (!this.selectedSecurity) return;
    const security = this.selectedSecurity;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .post<
        EODHDRealTimeResponseModel[]
      >(this.traderApiBase + "/TraderRealTimePricesL2", security, { headers })
      .subscribe({
        next: (data) => {
          if (this.selectedSecurity !== security) return;
          this.priceL2 = data ?? [];
        },
        error: (err) => console.error("TraderRealTimePricesL2 error:", err),
      });
  }

  private startRealtimePolling(): void {
    this.stopRealtimePolling();
    this.loadRealTimePricesL1();
    this.loadRealTimePricesL2();
    this.realtimePollInterval = setInterval(() => {
      this.loadRealTimePricesL1();
      this.loadRealTimePricesL2();
    }, 5000);
  }

  private stopRealtimePolling(): void {
    if (this.realtimePollInterval) {
      clearInterval(this.realtimePollInterval);
      this.realtimePollInterval = null;
    }
  }

  private getChartDateRange(): { fromDate: Date; toDate: Date } {
    const toDate = new Date();
    const fromDate = new Date();
    switch (this.selectedTimeframe) {
      case "1W":
        fromDate.setDate(fromDate.getDate() - 7);
        break;
      case "1M":
        fromDate.setMonth(fromDate.getMonth() - 1);
        break;
      case "6M":
        fromDate.setMonth(fromDate.getMonth() - 6);
        break;
      case "1Y":
        fromDate.setFullYear(fromDate.getFullYear() - 1);
        break;
      case "3Y":
        fromDate.setFullYear(fromDate.getFullYear() - 3);
        break;
      default:
        fromDate.setFullYear(fromDate.getFullYear() - 1);
    }
    return { fromDate, toDate };
  }

  private clearChartSeries(): void {
    if (this.candlestickSeries) this.candlestickSeries.setData([]);
    if (this.volumeSeries) this.volumeSeries.setData([]);
    if (this.ema20Series) this.ema20Series.setData([]);
    if (this.ema50Series) this.ema50Series.setData([]);
    if (this.ema200Series) this.ema200Series.setData([]);
    if (this.bbUpperSeries) this.bbUpperSeries.setData([]);
    if (this.bbLowerSeries) this.bbLowerSeries.setData([]);
    this.currentCandleData = [];
    this.currentVolumeData = [];
    this.currentEma20Data = [];
    this.currentEma50Data = [];
    this.currentEma200Data = [];
    this.currentBBData = { upper: [], lower: [] };
  }

  private applyChartData(
    data: EODHDEODResponseModel[],
    fromDate?: Date,
    toDate?: Date,
  ): void {
    const fromDay =
      fromDate != null ? this.eodDateToUtcDaySeconds(fromDate) : null;
    const toDay = toDate != null ? this.eodDateToUtcDaySeconds(toDate) : null;

    const sorted = data
      .filter((d) => {
        if (d.close == null) return false;
        if (fromDay != null && toDay != null) {
          const day = this.eodDateToUtcDaySeconds(d.date);
          return day >= fromDay && day <= toDay;
        }
        return true;
      })
      .sort(
        (a, b) =>
          this.eodDateToUtcDaySeconds(a.date) -
          this.eodDateToUtcDaySeconds(b.date),
      );

    this.currentCandleData = sorted.map((d) => {
      const time = this.eodDateToUtcDaySeconds(d.date);
      const close = d.close ?? 0;
      return {
        time,
        open: d.open ?? close,
        high: d.high ?? close,
        low: d.low ?? close,
        close,
      };
    });

    this.currentVolumeData = sorted.map((d, i) => {
      const candle = this.currentCandleData[i];
      return {
        time: candle.time,
        value: d.volume ?? 0,
        color:
          candle.close >= candle.open
            ? "rgba(26,135,0,0.5)"
            : "rgba(209,58,58,0.5)",
      };
    });

    this.currentEma20Data = this.calculateEMA(this.currentCandleData, 20);
    this.currentEma50Data = this.calculateEMA(this.currentCandleData, 50);
    this.currentEma200Data = this.calculateEMA(this.currentCandleData, 200);
    this.currentBBData = this.calculateBollingerBands(
      this.currentCandleData,
      20,
      2,
    );

    this.candlestickSeries.setData(this.currentCandleData);
    if (this.volumeSeries) this.volumeSeries.setData(this.currentVolumeData);
    if (this.ema20Series) this.ema20Series.setData(this.currentEma20Data);
    if (this.ema50Series) this.ema50Series.setData(this.currentEma50Data);
    if (this.ema200Series) this.ema200Series.setData(this.currentEma200Data);
    if (this.bbUpperSeries)
      this.bbUpperSeries.setData(this.currentBBData.upper);
    if (this.bbLowerSeries)
      this.bbLowerSeries.setData(this.currentBBData.lower);

    if (this.currentCandleData.length >= 2) {
      const last = this.currentCandleData[this.currentCandleData.length - 1];
      const prev = this.currentCandleData[this.currentCandleData.length - 2];

      if (!this.priceL1) {
        this.currentPrice = last.close;
        this.priceChange = last.close - prev.close;
        this.priceChangePercent =
          prev.close > 0 ? (this.priceChange / prev.close) * 100 : 0;
      }

      if (this.tradePrice <= 0) {
        this.tradePrice = last.close;
      }
    }

    if (
      fromDay != null &&
      toDay != null &&
      this.chart &&
      this.currentCandleData.length > 0
    ) {
      const rangeFrom = fromDay;
      const rangeTo = toDay + 24 * 60 * 60;
      this.chart
        .timeScale()
        .setVisibleRange({ from: rangeFrom as any, to: rangeTo as any });
    }
  }

  private eodDateToUtcDaySeconds(value: string | Date): number {
    if (typeof value === "string") {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
      if (m) {
        return Date.UTC(+m[1], +m[2] - 1, +m[3]) / 1000;
      }
    }
    const d = value instanceof Date ? value : new Date(value);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 1000;
  }

  private calculateEMA(data: any[], period: number): any[] {
    if (!data || data.length === 0) return [];
    const ema: any[] = [];
    const multiplier = 2 / (period + 1);
    let emaValue = data[0].close;

    for (let i = 0; i < data.length; i++) {
      emaValue = (data[i].close - emaValue) * multiplier + emaValue;
      ema.push({ time: data[i].time, value: emaValue });
    }

    return ema;
  }

  private calculateBollingerBands(data: any[], period: number, stdDev: number) {
    const upper: any[] = [];
    const lower: any[] = [];

    for (let i = period - 1; i < data.length; i++) {
      const slice = data.slice(i - period + 1, i + 1);
      const avg = slice.reduce((sum, c) => sum + c.close, 0) / period;
      const variance =
        slice.reduce((sum, c) => sum + Math.pow(c.close - avg, 2), 0) / period;
      const std = Math.sqrt(variance);

      upper.push({ time: data[i].time, value: avg + stdDev * std });
      lower.push({ time: data[i].time, value: avg - stdDev * std });
    }

    return { upper, lower };
  }

  private setupRealtimeUpdates(): void {
    this.drawingRedrawInterval = setInterval(() => {
      this.redrawAllDrawings();
    }, 5000);

    this.chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      this.redrawAllDrawings();
    });
  }

  changeTimeframe(tf: string): void {
    this.selectedTimeframe = tf;
    this.loadChartData();
  }

  private applyMobileIndicatorDefaults(): void {
    if (window.innerWidth >= 768) return;
    const mobileOff = ["EMA 50", "EMA 200", "Bollinger Bands"];
    this.indicators
      .filter((ind) => mobileOff.includes(ind.name) && ind.enabled)
      .forEach((ind) => this.toggleIndicator(ind));
  }

  toggleIndicator(indicator: any): void {
    indicator.enabled = !indicator.enabled;

    switch (indicator.name) {
      case "EMA 20":
        if (indicator.enabled) {
          this.ema20Series = this.chart.addLineSeries({
            color: "#2962FF",
            lineWidth: 2,
            title: "EMA 20",
          });
          this.ema20Series.setData(this.currentEma20Data);
        } else if (this.ema20Series) {
          this.chart.removeSeries(this.ema20Series);
          this.ema20Series = null;
        }
        break;

      case "EMA 50":
        if (indicator.enabled) {
          this.ema50Series = this.chart.addLineSeries({
            color: "#FF6D00",
            lineWidth: 2,
            title: "EMA 50",
          });
          this.ema50Series.setData(this.currentEma50Data);
        } else if (this.ema50Series) {
          this.chart.removeSeries(this.ema50Series);
          this.ema50Series = null;
        }
        break;

      case "EMA 200":
        if (indicator.enabled) {
          this.ema200Series = this.chart.addLineSeries({
            color: "#E91E63",
            lineWidth: 2,
            title: "EMA 200",
          });
          this.ema200Series.setData(this.currentEma200Data);
        } else if (this.ema200Series) {
          this.chart.removeSeries(this.ema200Series);
          this.ema200Series = null;
        }
        break;

      case "Bollinger Bands":
        if (indicator.enabled) {
          this.bbUpperSeries = this.chart.addLineSeries({
            color: "rgba(156, 39, 176, 0.5)",
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            title: "BB Upper",
          });
          this.bbLowerSeries = this.chart.addLineSeries({
            color: "rgba(156, 39, 176, 0.5)",
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            title: "BB Lower",
          });
          this.bbUpperSeries.setData(this.currentBBData.upper);
          this.bbLowerSeries.setData(this.currentBBData.lower);
        } else if (this.bbUpperSeries && this.bbLowerSeries) {
          this.chart.removeSeries(this.bbUpperSeries);
          this.chart.removeSeries(this.bbLowerSeries);
          this.bbUpperSeries = null;
          this.bbLowerSeries = null;
        }
        break;

      case "Volume":
        if (indicator.enabled) {
          this.volumeSeries = this.chart.addHistogramSeries({
            color: "#26a69a",
            priceFormat: { type: "volume" },
            priceScaleId: "volume",
          });
          this.chart
            .priceScale("volume")
            .applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
          this.volumeSeries.setData(this.currentVolumeData);
        } else if (this.volumeSeries) {
          this.chart.removeSeries(this.volumeSeries);
          this.volumeSeries = null;
        }
        break;
    }
  }

  private initDrawingCanvas(): void {
    if (!this.drawingCanvas) return;
    const canvas = this.drawingCanvas.nativeElement;
    const container = this.chartContainer.nativeElement;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight || 500;
    this.ctx = canvas.getContext("2d");
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
  }

  private resizeDrawingCanvas(): void {
    if (!this.drawingCanvas) return;
    const canvas = this.drawingCanvas.nativeElement;
    const container = this.chartContainer.nativeElement;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight || 500;
    this.redrawAllDrawings();
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.resizeDrawingCanvas();
  }

  onCanvasMouseDown(event: MouseEvent): void {
    if (!this.selectedDrawingTool) return;
    const rect = this.drawingCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const timeScale = this.chart.timeScale();
    const timeValue = timeScale.coordinateToTime(x);
    if (timeValue === null) return;
    const time = timeValue as number;
    const canvasHeight = rect.height;
    const visibleRange = this.getVisiblePriceRange();
    const price =
      visibleRange.max -
      (y / canvasHeight) * (visibleRange.max - visibleRange.min);
    this.isDrawing = true;
    this.drawingStartPoint = { x, y, time, price };
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.isDrawing || !this.drawingStartPoint || !this.selectedDrawingTool)
      return;
    const rect = this.drawingCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    this.redrawAllDrawings();
    this.drawPreview(this.drawingStartPoint.x, this.drawingStartPoint.y, x, y);
  }

  onCanvasMouseUp(event: MouseEvent): void {
    if (!this.isDrawing || !this.drawingStartPoint || !this.selectedDrawingTool)
      return;
    const rect = this.drawingCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const timeScale = this.chart.timeScale();
    const timeValue = timeScale.coordinateToTime(x);
    if (timeValue === null) return;
    const time = timeValue as number;
    const canvasHeight = rect.height;
    const visibleRange = this.getVisiblePriceRange();
    const price =
      visibleRange.max -
      (y / canvasHeight) * (visibleRange.max - visibleRange.min);
    const drawing: Drawing = {
      id: Date.now().toString(),
      type: this.selectedDrawingTool,
      points: [
        {
          time: this.drawingStartPoint.time,
          price: this.drawingStartPoint.price,
        },
        { time, price },
      ],
      color: this.drawingColor,
    };
    this.drawings.push(drawing);
    this.isDrawing = false;
    this.drawingStartPoint = null;
    this.redrawAllDrawings();
  }

  private drawPreview(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.ctx) return;
    this.ctx.strokeStyle = this.drawingColor;
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([]);
    switch (this.selectedDrawingTool) {
      case "trendline":
        this.ctx.beginPath();
        this.ctx.moveTo(x1, y1);
        this.ctx.lineTo(x2, y2);
        this.ctx.stroke();
        break;
      case "horizontal":
        this.ctx.beginPath();
        this.ctx.moveTo(0, y1);
        this.ctx.lineTo(this.drawingCanvas.nativeElement.width, y1);
        this.ctx.stroke();
        break;
      case "vertical":
        this.ctx.beginPath();
        this.ctx.moveTo(x1, 0);
        this.ctx.lineTo(x1, this.drawingCanvas.nativeElement.height);
        this.ctx.stroke();
        break;
      case "rectangle":
        this.ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        this.ctx.fillStyle = this.drawingColor + "20";
        this.ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
        break;
      case "fibonacci":
        this.drawFibonacci(x1, y1, x2, y2);
        break;
      case "arrow":
        this.drawArrow(x1, y1, x2, y2);
        break;
    }
  }

  private redrawAllDrawings(): void {
    if (!this.ctx || !this.drawingCanvas) return;
    this.ctx.clearRect(
      0,
      0,
      this.drawingCanvas.nativeElement.width,
      this.drawingCanvas.nativeElement.height,
    );
    const timeScale = this.chart.timeScale();
    const visibleRange = this.getVisiblePriceRange();
    const canvasHeight = this.drawingCanvas.nativeElement.height;

    this.drawings.forEach((drawing) => {
      if (drawing.points.length < 2) return;
      const x1Coord = timeScale.timeToCoordinate(drawing.points[0].time as any);
      const x2Coord = timeScale.timeToCoordinate(drawing.points[1].time as any);
      if (x1Coord === null || x2Coord === null) return;
      const x1 = x1Coord as number;
      const x2 = x2Coord as number;
      const y1 =
        ((visibleRange.max - drawing.points[0].price) /
          (visibleRange.max - visibleRange.min)) *
        canvasHeight;
      const y2 =
        ((visibleRange.max - drawing.points[1].price) /
          (visibleRange.max - visibleRange.min)) *
        canvasHeight;

      this.ctx.strokeStyle = drawing.color;
      this.ctx.lineWidth = 2;
      this.ctx.setLineDash([]);

      switch (drawing.type) {
        case "trendline":
          this.ctx.beginPath();
          this.ctx.moveTo(x1, y1);
          this.ctx.lineTo(x2, y2);
          this.ctx.stroke();
          break;
        case "horizontal":
          this.ctx.beginPath();
          this.ctx.moveTo(0, y1);
          this.ctx.lineTo(this.drawingCanvas.nativeElement.width, y1);
          this.ctx.stroke();
          break;
        case "vertical":
          this.ctx.beginPath();
          this.ctx.moveTo(x1, 0);
          this.ctx.lineTo(x1, this.drawingCanvas.nativeElement.height);
          this.ctx.stroke();
          break;
        case "rectangle":
          this.ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
          this.ctx.fillStyle = drawing.color + "20";
          this.ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
          break;
        case "fibonacci":
          this.drawFibonacci(x1, y1, x2, y2);
          break;
        case "arrow":
          this.drawArrow(x1, y1, x2, y2);
          break;
      }
    });
  }

  private getVisiblePriceRange(): { min: number; max: number } {
    if (!this.currentCandleData || this.currentCandleData.length === 0)
      return { min: 0, max: 100 };
    const timeScale = this.chart.timeScale();
    const visibleLogicalRange = timeScale.getVisibleLogicalRange();
    if (!visibleLogicalRange) {
      const prices: number[] = [];
      this.currentCandleData.forEach((c) => prices.push(c.high, c.low));
      return { min: Math.min(...prices), max: Math.max(...prices) };
    }
    const from = Math.max(0, Math.floor(visibleLogicalRange.from));
    const to = Math.min(
      this.currentCandleData.length - 1,
      Math.ceil(visibleLogicalRange.to),
    );
    const visibleData = this.currentCandleData.slice(from, to + 1);
    const prices: number[] = [];
    visibleData.forEach((c) => prices.push(c.high, c.low));
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }

  private drawFibonacci(x1: number, y1: number, x2: number, y2: number): void {
    const levels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
    const height = y2 - y1;
    this.ctx.font = "11px -apple-system, sans-serif";
    this.ctx.fillStyle = this.drawingColor;
    levels.forEach((level) => {
      const y = y1 + height * level;
      this.ctx.setLineDash([5, 5]);
      this.ctx.beginPath();
      this.ctx.moveTo(x1, y);
      this.ctx.lineTo(x2, y);
      this.ctx.stroke();
      this.ctx.fillText(`${(level * 100).toFixed(1)}%`, x2 + 5, y + 4);
    });
  }

  private drawArrow(x1: number, y1: number, x2: number, y2: number): void {
    const headLength = 15;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(x2, y2);
    this.ctx.lineTo(
      x2 - headLength * Math.cos(angle - Math.PI / 6),
      y2 - headLength * Math.sin(angle - Math.PI / 6),
    );
    this.ctx.moveTo(x2, y2);
    this.ctx.lineTo(
      x2 - headLength * Math.cos(angle + Math.PI / 6),
      y2 - headLength * Math.sin(angle + Math.PI / 6),
    );
    this.ctx.stroke();
  }

  selectDrawingTool(toolId: string): void {
    if (this.selectedDrawingTool === toolId) {
      this.selectedDrawingTool = null;
      this.drawingTools.forEach((t) => (t.active = false));
      this.drawingCanvas.nativeElement.style.pointerEvents = "none";
    } else {
      this.selectedDrawingTool = toolId;
      this.drawingTools.forEach((t) => (t.active = t.id === toolId));
      this.drawingCanvas.nativeElement.style.pointerEvents = "auto";
      this.reapplyLocalize();
    }
  }

  clearAllDrawings(): void {
    this.drawings = [];
    if (this.ctx)
      this.ctx.clearRect(
        0,
        0,
        this.drawingCanvas.nativeElement.width,
        this.drawingCanvas.nativeElement.height,
      );
  }

  deleteLastDrawing(): void {
    this.drawings.pop();
    this.redrawAllDrawings();
  }

  resetChart(): void {
    if (this.chart && this.chart.timeScale) {
      this.chart.timeScale().fitContent();
    }
  }


  blockNonIntegerKeys(event: KeyboardEvent): void {
    if ([".", ",", "e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
    }
  }

  onAmountChange(amount: number): void {
    this.tradeAmount = Math.max(0, Math.floor(+amount || 0));
    this.tradeTotal =
      (this.effectivePrice * this.tradeAmount * this.secContractSize) /
      this.secFactor; // security currency
    this.sliderPercent =
      this.availableLiquidity > 0
        ? Math.min(
            100,
            ((this.tradeTotal * this.fxRate) / this.availableLiquidity) * 100,
          )
        : 0;
    this.liveCheckAmounts = null;
    if (this.tradeAmount > 0 && this.selectedSecurity) {
      this.liveCheckLoading = true;
      this.liveCheckSubject.next();
    }
  }

  onPriceChange(price: number): void {
    this.tradePrice = price;
    this.tradeTotal =
      (this.tradePrice * this.tradeAmount * this.secContractSize) /
      this.secFactor; // security currency
    this.sliderPercent =
      this.availableLiquidity > 0
        ? Math.min(
            100,
            ((this.tradeTotal * this.fxRate) / this.availableLiquidity) * 100,
          )
        : 0;
    this.liveCheckAmounts = null;
    if (this.tradeAmount > 0 && this.selectedSecurity) {
      this.liveCheckLoading = true;
      this.liveCheckSubject.next();
    }
  }

  onStopPriceChange(price: number): void {
    this.tradeStopPrice = price;
  }

  onSliderChange(percent: number): void {
    if (this.sliderDisabled) {
      this.sliderPercent = 0;
      return;
    }
    const rate = this.fxRate;
    const price = this.effectivePrice;
    if (price > 0 && rate > 0) {
      this.tradeAmount = Math.floor(
        (((this.availableLiquidity * percent) / 100) * this.secFactor) /
          (price * rate * this.secContractSize),
      );
    }
    this.tradeTotal =
      (price * this.tradeAmount * this.secContractSize) / this.secFactor;

    if (this.availableLiquidity > 0) {
      this.sliderPercent = Math.min(
        100,
        ((this.tradeTotal * rate) / this.availableLiquidity) * 100,
      );
    } else {
      this.sliderPercent = 0;
    }
    this.liveCheckAmounts = null;
    if (this.tradeAmount > 0 && this.selectedSecurity) {
      this.liveCheckLoading = true;
      this.liveCheckSubject.next();
    }
  }

  private runLiveCheck(): void {
    if (!this.selectedSecurity || this.tradeAmount <= 0) {
      this.liveCheckLoading = false;
      return;
    }
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .post<{
        success: boolean;
        description: string;
        amounts: { [key: string]: number };
      }>(
        this.traderApiBase + "/TraderOrderCheck",
        this.buildTraderOrder(this.marketOnlyForSell ? "Sell" : "Buy"),
        { headers },
      )
      .subscribe({
        next: (res) => {
          this.liveCheckLoading = false;

          if (res && res.amounts && Object.keys(res.amounts).length > 0) {
            this.liveCheckAmounts = res.amounts;
          } else {
            this.liveCheckAmounts = null;
          }
          this.reapplyLocalize();
        },
        error: () => {
          this.liveCheckLoading = false;
          this.liveCheckAmounts = null;
        },
      });
  }

  private toLocalISOString(d: Date): string {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }

  private getTransactionType(): number {
    return this.selectedOrderType;
  }

  private getOrderPeriod(): number {
    if (this.isImmediateOrder) return 1;
    switch (this.selectedValidity) {
      case "Day":
        return 3;
      case "GTC":
        return 6;
      case "GTD":
        return 4;
      default:
        return 3;
    }
  }

  private buildTraderOrder(side: "Buy" | "Sell"): any {
    const expiryDate =
      this.showDatePicker && this.goodTillDate
        ? `${this.goodTillDate}T00:00:00`
        : this.toLocalISOString(new Date());
    return {
      ...this.selectedSecurity,
      OrderType: side === "Buy" ? 0 : 1,
      TradingType: 1,
      OrderPeriod: this.getOrderPeriod(),
      TransactionType: this.getTransactionType(),
      SecurityIndex: this.selectedSecurity?.SecIndex ?? 0,
      PartnerIndex: this._authService.UserInfo?.PartnerIndex ?? 0,
      Quantity: this.tradeAmount,
      Price: this.isImmediateOrder
        ? side === "Buy" && this.selectedOrderType === 1
          ? Math.round(this.currentPrice * this.secReservationPercent * 100) /
            100
          : this.currentPrice
        : this.tradePrice,
      StopPrice: this.selectedOrderType === 7 ? this.tradeStopPrice : null,
      ExpiryDate: expiryDate,
    };
  }

  buyAsset(): void {
    if (!this.canTrade) return;
    this.openOrderConfirm("Buy");
  }

  sellAsset(): void {
    if (!this.canSell) return;
    this.openOrderConfirm("Sell");
  }

  openOrderConfirm(side: "Buy" | "Sell"): void {
    this.confirmOrderSide = side;
    this.orderResult = null;
    this.orderError = "";
    this.orderCheckAmounts = null;
    this.orderCheckError = "";
    this.showOrderConfirm = true;
    this.reapplyLocalize();

    this.isCheckingOrder = true;
    this._authService.getUserInfo().subscribe({
      next: () => this.runOrderCheck(side),
      error: () => {
        this.isCheckingOrder = false;
      },
    });
  }

  private runOrderCheck(side: "Buy" | "Sell"): void {
    this.isCheckingOrder = true;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .post<{
        success: boolean;
        description: string;
        amounts: { [key: string]: number };
      }>(
        this.traderApiBase + "/TraderOrderCheck",
        this.buildTraderOrder(side),
        { headers },
      )
      .subscribe({
        next: (res) => {
          this.isCheckingOrder = false;
          if (res && res.success) {
            this.orderCheckAmounts = res.amounts;
          } else {
            this.orderCheckError =
              res && res.description
                ? res.description
                : this._localize.getLocalizedResource("Res1144") ||
                  "Order check failed.";
          }
          this.reapplyLocalize();
        },
        error: (err) => {
          this.isCheckingOrder = false;
          this.orderCheckError =
            err.message ??
            (this._localize.getLocalizedResource("Res1145") ||
              "Error checking order.");
          this.reapplyLocalize();
          console.error("TraderOrderCheck error:", err);
        },
      });
  }

  confirmOrder(): void {
    if (
      !this.selectedSecurity ||
      this.isSubmittingOrder ||
      this.isCheckingOrder ||
      !this.confirmOrderSide
    )
      return;
    if (this.confirmOrderSide === "Buy" && !this.canTrade) return;
    if (this.confirmOrderSide === "Sell" && !this.canSell) return;
    if (!this.orderCheckAmounts || !!this.orderCheckError) return;
    this.isSubmittingOrder = true;
    this.orderResult = null;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .post<TraderOrderResponseModel>(
        this.traderApiBase + "/TraderOrderInsert",
        this.buildTraderOrder(this.confirmOrderSide),
        { headers },
      )
      .subscribe({
        next: (res) => {
          this.isSubmittingOrder = false;
          this.orderResult = {
            success: res.success,
            description: res.description,
          };
          if (res.success) {
            this.tradeAmount = 0;
            this.tradeTotal = 0;
            this.sliderPercent = 0;
            this.liveCheckAmounts = null;

            this.loadCashBalanceForSecurity(this.selectedSecurity);
            this.loadPositionQuantityForSecurity(this.selectedSecurity);

            this._insaService.createReport(true);
          }
          this.reapplyLocalize();
        },
        error: (err) => {
          this.isSubmittingOrder = false;
          this.orderResult = {
            success: false,
            description:
              err.message ??
              (this._localize.getLocalizedResource("Res1146") ||
                "Error submitting order."),
          };
          this.reapplyLocalize();
          console.error("TraderOrderInsert error:", err);
        },
      });
  }

  closeOrderConfirm(): void {
    if (this.isSubmittingOrder) return;
    this.showOrderConfirm = false;
    this.confirmOrderSide = null;
    this.orderResult = null;
    this.orderCheckAmounts = null;
    this.orderCheckError = "";
    this.isCheckingOrder = false;
  }

  selectAsset(asset: TraderSecurityDtoModel): void {
    this.selectedSecurity = asset;
    this.selectedSymbol =
      asset.SecSymbol?.trim() || asset.SecNumber?.trim() || "";
    this.selectedAssetName = asset.SecName?.trim() || "";
    this.isDomestic =
      asset.SecStockExchange === 12 || asset.SecStockExchange === 15;
    this.allowedTransactionTypes = [];
    this.allowedOrderTypes = [];
    this.loadAllowedOrderTypes(asset.SecStockExchange);
    this.currentPrice = 0;
    this.secReservationPercent = 1;
    this.priceChange = 0;
    this.priceChangePercent = 0;
    this.priceL1 = null;
    this.priceL2 = [];
    this.showSearchResults = false;
    this.searchQuery = "";
    this.filteredAssets = [];
    this.orderError = "";
    this.liveCheckAmounts = null;
    this.liveCheckLoading = false;
    // Reset all trade inputs when switching security
    this.tradeAmount = 0;
    this.tradePrice = 0;
    this.tradeStopPrice = 0;
    this.tradeTotal = 0;
    this.sliderPercent = 0;
    if (this.candlestickSeries) {
      this.loadChartData();
    }
    this.startRealtimePolling();
    this.loadPositionQuantityForSecurity(asset);
    this.loadCashBalanceForSecurity(asset);
    this.loadSecReservationPercent(asset.SecIndex);
    this.reapplyLocalize();
  }

  private loadPositionQuantityForSecurity(
    security: TraderSecurityDtoModel,
  ): void {
    const token = ++this._positionQuantityToken;
    this.heldQuantityLoading = true;
    this.heldQuantity = 0;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .post<number>(
        this.traderApiBase + "/PositionQuantityForSecurity",
        security,
        { headers },
      )
      .subscribe({
        next: (quantity) => {
          if (token !== this._positionQuantityToken) return;
          this.heldQuantityLoading = false;
          this.heldQuantity = quantity ?? 0;
        },
        error: () => {
          if (token !== this._positionQuantityToken) return;
          this.heldQuantityLoading = false;
          this.heldQuantity = 0;
        },
      });
  }

  filterAssets(): void {
    this.searchSubject.next(this.searchQuery);
  }

  private getChartTheme() {
    const color = localStorage.getItem("color") || "default";
    const theme = appConfig.chartThemes[color] || appConfig.chartThemes.default;
    // Canvas graf ne nasljeđuje CSS font - preuzmi font klijenta s body-ja
    // (NLB: CocoSharp).
    return {
      ...theme,
      layout: {
        ...theme.layout,
        fontFamily: window.getComputedStyle(document.body).fontFamily,
      },
    };
  }

  hideSearchResults(): void {
    setTimeout(() => {
      this.showSearchResults = false;
      this.searchQuery = "";
    }, 200);
  }

  private loadCashBalanceForSecurity(security: TraderSecurityDtoModel): void {
    const token = ++this._cashBalanceToken;
    this.cashBalanceLoading = true;
    this.availableLiquidity = 0;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .post<number>(this.traderApiBase + "/CashBalanceForSecurity", security, {
        headers,
        params: { currency: this.liquidityCurrency },
      })
      .subscribe({
        next: (balance) => {
          if (token !== this._cashBalanceToken) return;
          this.cashBalanceLoading = false;
          this.availableLiquidity = balance ?? 0;
        },
        error: () => {
          if (token !== this._cashBalanceToken) return;
          this.cashBalanceLoading = false;
        },
      });
  }

  private loadCheckCashBalanceForUser(): void {
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .get<boolean>(this.traderApiBase + "/CheckCashBalanceForUser", {
        headers,
      })
      .subscribe({
        next: (check) => {
          this.checkCashBalance = check !== false;
        },
        error: () => {
          this.checkCashBalance = true;
        },
      });
  }

  private loadSecReservationPercent(secIndex: number): void {
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .get<number>(this.traderApiBase + "/GetSecReservationPercent", {
        headers,
        params: { secIndex: secIndex.toString() },
      })
      .subscribe({
        next: (percent) => {
          this.secReservationPercent =
            percent != null && percent > 0 ? percent : 1;
        },
        error: () => {
          this.secReservationPercent = 1;
        },
      });
  }

  private loadAllowedOrderTypes(secStockExchange: number): void {
    const token = ++this._orderTypesToken;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .get<number[]>(this.traderApiBase + "/GetOrderTranTypeForSE", {
        headers,
        params: { secStockExchange: secStockExchange.toString() },
      })
      .subscribe({
        next: (types) => {
          if (token !== this._orderTypesToken) return;

          this.allowedTransactionTypes = [
            ...new Set((types ?? []).map((t) => +t)),
          ];
          const first = this.allowedTransactionTypes[0];
          if (first !== undefined) this.onOrderTypeChange(first);
        },
        error: () => {
          if (token !== this._orderTypesToken) return;
          this.allowedTransactionTypes = [];
        },
      });
  }

  private loadAllowedOrderTypesForTransaction(
    secStockExchange: number,
    transactionType: number,
  ): void {
    const token = ++this._allowedOrderTypesToken;
    const headers = this._authService.getAuthHeaders("application/json");
    this._http
      .get<number[]>(this.traderApiBase + "/GetOrderTypeForSEAndTranType", {
        headers,
        params: {
          secStockExchange: secStockExchange.toString(),
          orderTransactionType: transactionType.toString(),
        },
      })
      .subscribe({
        next: (types) => {
          if (token !== this._allowedOrderTypesToken) return;
          this.allowedOrderTypes = (types ?? []).map((t) => +t);
        },
        error: () => {
          if (token !== this._allowedOrderTypesToken) return;
          this.allowedOrderTypes = [];
        },
      });
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-trader/insa-trader.component.ts